import {
  ColDef,
  ColGroupDef,
  FilterChangedEvent,
  FilterModel,
  GridState,
  SortModelItem,
} from 'ag-grid-community';
import { camelCase } from 'lodash';
import { useCallback, useMemo } from 'react';

import { useAuthUser } from 'auth/hooks';
import { MANAGE_FILTERS_EXPIRATION_TIME } from 'core/constants';
import { TableLayoutType } from 'core/enums';
import { LocalStorageKeys } from 'core/enums';
import { useLocalStorage } from 'core/hooks';
import { getAgGridFilterConfig, jsonToObject, trimTextFilterModel } from 'core/utils';

interface IProps {
  layoutType: TableLayoutType;
  initialState?: GridState;
  hasManageFilters: boolean;
  columnDefs?: ColDef[];
}

// The model type each built-in AG Grid filter saves, so a saved model can be checked against the
// column it is about to be restored on.
const FILTER_MODEL_TYPES: Record<string, string> = {
  agDateColumnFilter: 'date',
  agNumberColumnFilter: 'number',
  agSetColumnFilter: 'set',
  agTextColumnFilter: 'text',
};

// Drops the saved filters a column cannot take any more: a column whose filter changed type since
// the model was saved (text to set, say) would make the grid throw while restoring it. Columns the
// grid no longer has, custom filters and models without a type pass through untouched.
export const dropStaleFilterModels = (
  filterModel: FilterModel | undefined,
  columnDefs: (ColDef | ColGroupDef)[] | undefined
): FilterModel | undefined => {
  if (!filterModel || !columnDefs?.length) {
    return filterModel;
  }

  // A group's columns count too: the family parts grid keeps its attribute columns in groups.
  const flatten = (columns: (ColDef | ColGroupDef)[]): ColDef[] =>
    columns.flatMap((column) => ('children' in column ? flatten(column.children ?? []) : [column]));

  const filterTypeByColumn = new Map(
    flatten(columnDefs)
      .filter((column) => typeof column.filter === 'string' && FILTER_MODEL_TYPES[column.filter])
      .map((column) => [column.colId ?? column.field, FILTER_MODEL_TYPES[column.filter as string]])
  );

  return Object.fromEntries(
    Object.entries(filterModel).filter(([colId, model]) => {
      const expectedType = filterTypeByColumn.get(colId);

      return !expectedType || !model?.filterType || model.filterType === expectedType;
    })
  );
};

export const useAgGridFilter = ({
  layoutType,
  initialState: initialStateProp = {},
  hasManageFilters,
  columnDefs,
}: IProps) => {
  const { user } = useAuthUser();
  const { saveObject, getObject } = useLocalStorage();

  if (hasManageFilters && !layoutType) {
    throw new Error('layoutType is required when hasManageFilters is true');
  }

  const localStorageKey = `${LocalStorageKeys.MANAGE_FILTERS_PREFIX}_${layoutType}`;

  const localStorageConfig = useMemo(() => {
    return getObject<{ filterModel: FilterModel; sortModel: SortModelItem[] }>(localStorageKey);
  }, [localStorageKey, getObject]);

  const defaultFilterConfig = useMemo(() => {
    if (localStorageConfig || !hasManageFilters || !user) {
      return {};
    }

    const config = user.defaultFilters?.[camelCase(layoutType)]?.config;

    return config ? jsonToObject(config) : {};
  }, [hasManageFilters, user, layoutType, localStorageConfig]);

  // Saved before the trimming existed, a restored text value may still carry its spaces.
  const initialFilterModel = useMemo(
    () =>
      trimTextFilterModel(
        dropStaleFilterModels(
          localStorageConfig ? localStorageConfig.filterModel : defaultFilterConfig?.filterModel,
          columnDefs
        )
      ),
    [localStorageConfig, defaultFilterConfig, columnDefs]
  );

  const initialSortModel = useMemo(
    () => (localStorageConfig ? localStorageConfig.sortModel : defaultFilterConfig?.sortModel),
    [localStorageConfig, defaultFilterConfig]
  );

  const initialState = useMemo(
    () => ({
      ...initialStateProp,
      filter: initialFilterModel ? { filterModel: initialFilterModel } : initialStateProp.filter,
      sort: initialSortModel ? { sortModel: initialSortModel } : initialStateProp.sort,
    }),
    [initialStateProp, initialFilterModel, initialSortModel]
  );

  const defaultFilterModel = useMemo(() => {
    if (hasManageFilters && user && user.defaultFilters?.[camelCase(layoutType)]?.config) {
      const config = jsonToObject(user.defaultFilters[camelCase(layoutType)].config);

      return trimTextFilterModel(config?.filterModel) || {};
    }

    return initialStateProp.filter?.filterModel || {};
  }, [hasManageFilters, user, layoutType, initialStateProp]);

  const saveFilterState = useCallback(
    (event: FilterChangedEvent) => {
      saveObject(localStorageKey, getAgGridFilterConfig(event.api), MANAGE_FILTERS_EXPIRATION_TIME);
    },
    [localStorageKey, saveObject]
  );

  return {
    initialState,
    saveFilterState,
    defaultFilterModel,
  };
};
